import { Button } from "@/components/ui/button"
import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { getPessoas } from "@/services/companyService"
import { PessoaDataType } from "@/types/company"
import { useLoading } from "@/hooks/useLoading"
import { BatchUploadModal } from "@/modules/Users/components/BatchUploadModal"
import { useAuth } from "@/modules/Auth/context/AuthContext"
import { UserCard } from "@/modules/Users/components/UserCard"
import { PageHeader } from "@/components/Molecules/PageHeader/PageHeader"
import { AnimatePresence, motion } from "framer-motion"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Icon } from "@/components/Atoms/Icon"
import { UsersFilter } from "@/modules/Users/components/UsersFilter"

export default function BackofficeUsers() {
  const [users, setUsers] = useState<PessoaDataType[]>([])
  const [filteredUsers, setFilteredUsers] = useState<PessoaDataType[]>([])
  const navigate = useNavigate()

  // --- States de Filtros ---
  const [statusFilter, setStatusFilter] = useState("ativos")
  const [searchQuery, setSearchQuery] = useState("")
  const [profileFilter, setProfileFilter] = useState("all")
  const [typeFilter, setTypeFilter] = useState("all")

  // --- States de Modais ---
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false)

  const { showLoading, hideLoading } = useLoading()
  const { user } = useAuth()

  // --- Navigation Handlers ---
  const handleEditUser = (user: PessoaDataType) => {
    navigate(`/users/edit/${user.pes_id}`)
  }

  const handleCreateUser = () => {
    navigate("/users/create")
  }

  // --- Batch Modal Handlers ---
  const handleOpenBatchModal = () => {
    setIsBatchModalOpen(true)
  }

  const handleCloseBatchModal = () => {
    setIsBatchModalOpen(false)
    fetchUsers() // Recarrega os usuários após o fechamento do modal de lote
  }

  const fetchUsers = () => {
    if (!user) return
    showLoading()
    getPessoas(user.empId)
      .then(({ data }) => {
        setUsers(data.data)
      })
      .catch((error) => {
        console.error(error)
      })
      .finally(() => {
        hideLoading()
      })
  }

  useEffect(() => {
    fetchUsers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  // --- Lógica de filtragem ---
  useEffect(() => {
    let processedUsers = [...users]

    if (statusFilter !== "all") {
      const isActive = statusFilter === "ativos"
      processedUsers = processedUsers.filter((user) => user.pes_status === isActive)
    }

    if (profileFilter !== "all") {
      processedUsers = processedUsers.filter((user) => user.perfil === profileFilter)
    }

    if (typeFilter !== "all") {
      const isAdmin = typeFilter === "1"
      processedUsers = processedUsers.filter((user) => user.admin === isAdmin)
    }

    if (searchQuery.trim() !== "") {
      const lowerCaseQuery = searchQuery.toLowerCase()
      processedUsers = processedUsers.filter(
        (user) =>
          user.pes_nome?.toLowerCase().includes(lowerCaseQuery) ||
          user.pes_email?.toLowerCase().includes(lowerCaseQuery)
      )
    }

    setFilteredUsers(processedUsers)
  }, [users, statusFilter, profileFilter, typeFilter, searchQuery])

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.98 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 260,
        damping: 20,
      },
    },
    exit: { opacity: 0, scale: 0.95, transition: { duration: 0.2 } },
  }

  const filterTypeDictionary = {
    0: "Padrão",
    1: "PAG",
  }

  const appliedFiltersCount = useMemo(() => {
    const filters = []

    if (statusFilter !== "all") filters.push(statusFilter)
    if (profileFilter !== "all") filters.push(profileFilter)
    if (typeFilter !== "all") filters.push(filterTypeDictionary[typeFilter])

    if (searchQuery.trim() !== "") filters.push(searchQuery)

    return filters
  }, [statusFilter, profileFilter, typeFilter, searchQuery])

  const Chip = ({ label }: { label: string }) => {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-orange-500 bg-orange-500/20 px-3 py-1 text-xs text-orange-500">
        <span>{label}</span>
        <button>
          <Icon
            state="outline"
            name="x"
            size={10}
          />
        </button>
      </span>
    )
  }

  return (
    <div className="animate-fade-in">
      <div className="flex flex-wrap items-center justify-between">
        <div className="flex-[5]">
          <PageHeader
            title="Usuários"
            icon="users"
          />
        </div>
        <div className="hidden flex-1 gap-3 px-4 sm:flex">
          <Button
            className="flex-1"
            variant="outline"
            onClick={handleOpenBatchModal}
          >
            Importar em Lote
          </Button>
          <Button
            className="flex-1"
            onClick={handleCreateUser}
          >
            <Icon name="add" />
            <span className="ml-2">Novo Usuário</span>
          </Button>
        </div>
        <div className="block px-4 sm:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="size-[40px] flex-1 rounded-full border border-orange-500 bg-transparent text-orange-500">
                <Icon
                  size={10}
                  name="plus"
                />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="mx-4">
              <DropdownMenuItem onClick={handleCreateUser}>Novo Usuário</DropdownMenuItem>
              <DropdownMenuItem onClick={handleOpenBatchModal}>Importar em Lote</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="px-6 py-4">
        <UsersFilter
          search={searchQuery}
          onSearchChange={setSearchQuery}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          profileFilter={profileFilter}
          onProfileFilterChange={setProfileFilter}
          typeFilter={typeFilter}
          onTypeFilterChange={setTypeFilter}
        />
      </div>

      {/* Cabeçalho da Lista */}
      <div className="p-6">
        <h2 className="text-lg font-semibold text-foreground">Usuários ({filteredUsers.length})</h2>
      </div>

      {/* Grid de Usuários */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={
          {
            "--max-width": filteredUsers.length <= 2 ? "400px" : "1fr",
          } as React.CSSProperties
        }
        className="grid grid-cols-[repeat(auto-fit,minmax(calc(5vw+200px),var(--max-width)))] gap-6 px-6"
      >
        <AnimatePresence mode="popLayout">
          {filteredUsers.map((user) => (
            <motion.div
              key={user.pes_id}
              variants={itemVariants}
              layout
            >
              <UserCard
                user={user}
                onEdit={handleEditUser}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

{/* Mensagem para quando não há resultados */}
      {filteredUsers.length === 0 && (
        <div className="card-elevated py-10 text-center">
          <i className="fi fi-rs-user-slash mb-4 text-5xl text-muted-foreground"></i>
          <h3 className="text-xl font-semibold">Nenhum usuário encontrado</h3>
          <p className="text-muted-foreground">
            Tente ajustar os filtros ou cadastre um novo usuário.
          </p>
        </div>
      )}

      {/* --- Renderização dos Modais --- */}
      {isBatchModalOpen && <BatchUploadModal onClose={handleCloseBatchModal} />}
    </div>
  )
}
